import {
  Box,
  Button,
  ClickAwayListener,
  debounce,
  IconButton,
  ListItem,
  Paper,
  PaperProps,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { Autocomplete } from '@components/Autocomplete';
import {
  border,
  buildDataTestId,
  buildDataTestIdAttribute,
  buildFullDataTestId,
  DATA_TEST_ID_SELECTION,
  log,
  replaceCebabCase,
} from '@src/utils';
import { ReactComponent as PopupIcon } from '@icons/popupIcon.svg';
import {
  FC,
  Fragment,
  InputHTMLAttributes,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Checkbox } from '@components/Checkbox';
import { ReactComponent as ArrowDown } from '@icons/Arrows/down.svg';
import { PAGE_SIZE } from '@src/api/common';
import { IProvider, providersApiSlice, setLaboratoriesScope, setProvidersScope } from '@src/api';
import { useParams } from 'react-router-dom';
import { FetchingContext } from '@pages/Selection/components/Visibility/FetchingContext';
import { IntersectionType } from '@src/api/types';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@src/store';
import {
  changeIdsChildren,
  checkChildrenWithBrothers,
  childrenIncludesInAddedRemovedArr,
  includesParentId,
  parentIncludesInAddedRemovedArr,
  parentWithoutId,
  putParent,
  removedChildrenFromAddedRemoved,
  removedParentFromAddedRemoved,
  setAddedRemovedArrChildren,
} from './helpers';

const SCOPE_PROVIDERS_NAME = 'isAllOrderingProviders';

type Option = {
  value: string;
  key?: string;
  name: string;
  isSelected?: boolean;
  providers?: {
    isSelected: boolean | undefined;
    name: string;
    id: string;
  }[];
};

type AutocompleteWithApplyProps = {
  value: { intersectionType: IntersectionType; ids: (string | { [key: string]: string[] })[] };
  placeholder?: string;
  options?: any[];
  noOptionsText?: string;
  startSearch?: () => void;
  intersectionName?: string;
  onApply?: (ids: (string | { [key: string]: string[] })[], addedIds?: any[], removedIds?: any[]) => void;
  parentName?: string;
  childName?: string;
  withChildren?: boolean;
  loading?: boolean;
  isEnd?: boolean;
  setValuesFilter?: (val: any) => void;
  inputValue?: string;
  onSearch?: (val: string) => void;
  isSuccess?: boolean;
  salesRepresentativeId?: string;
  addedArr: any[];
  removedArr: any[];
  initialIntersectionType?: Nullable<IntersectionType>;
  intersectionTypeFormValue?: Nullable<IntersectionType>;
  searchValue?: Nullable<string>;
  inputPlaceholder?: string;
  disabled?: boolean;
  scopeName: string;
};

const classes = {
  button: {
    width: '78px',
    fontWeight: 500,
    fontSize: '14px',
    height: '30px',
    padding: 0,
    minWidth: '0',
  },
};

const OptionItem = ({
  option,
  state,
  changeAll,
  changeCheck,
  checked,
  sx,
  changeCheckChildren,
  isChildren,
  childrenCheckedIds,
  withChildren,
  changeAddedRemovedArray,
  changeAddedRemovedArrayChildren,
  salesRepresentativeId,
  intersctionValue,
  checkedValues,
  childrenSetCheckChilds,
  clearAll,
  allChecked,
  isClearAddedRemoved,
  addedIds,
  removedIds,
  updateProviderDisableStates,
  dataTestId,
  ...props
}: {
  option: Option;
  checkedValues?: any;
  addedIds?: any[];
  removedIds?: any[];
  setCheckChilds?;
  dataTestId?: string;
  state?;
  childrenSetCheckChilds?;
  changeAll: (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  changeCheck: (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  changeCheckChildren?: (
    e: React.ChangeEvent<HTMLInputElement>,
    parentId: string,
    check: boolean,
    brothersIds: string[],
  ) => void;
  checked: boolean;
  sx?: SxProps;
  childrenCheckedIds?: string[];
  isChildren?: boolean;
  withChildren?: boolean;
  changeAddedRemovedArrayChildren?: (
    checked: boolean,
    childrenId: string,
    parentId: string,
    brothersIds: string[],
    checkChilds: Nullable<string[]>,
  ) => void;
  changeAddedRemovedArray?: (optId: string, checked: boolean) => void;
  salesRepresentativeId?: string;
  intersctionValue?: IntersectionType;
  clearAll?: boolean;
  allChecked?: boolean;
  isClearAddedRemoved?: boolean;
  updateProviderDisableStates?: (
    localChecked: boolean,
    checkChilds: Nullable<string[]>,
    data: IProvider[] | undefined,
    option: Option,
  ) => void;
}) => {
  const { selectionId } = useParams();

  const [open, setOpen] = useState(option.providers && option.providers?.length > 0);

  const [localChecked, setLocalChecked] = useState(checked);

  const [checkChilds, setCheckChilds] = useState<Nullable<string[]>>(null);

  useEffect(() => {
    setLocalChecked(checked);
  }, [checked]);

  const [fetchProviders, { data }] = providersApiSlice.useLazyGetProvidersQuery();

  useEffect(() => {
    updateProviderDisableStates && updateProviderDisableStates(localChecked, checkChilds, data, option);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localChecked, checkChilds, data]);
  const key = typeof option?.value === 'string' ? option?.value : option.key;

  const providersArray = option.providers && option.providers?.length > 0 ? option.providers : data;
  const onClick = () => {
    if (isChildren || state?.index === 0) {
      return;
    }
    if (!open && withChildren && !option.providers?.length)
      fetchProviders({
        facilityId: option.value,
        salesRepresentativeAccountId: salesRepresentativeId || null,
        ...(selectionId ? { selectionId: +selectionId } : {}),
      })
        .then(res => {
          const brothersIds = res?.data?.map(({ id }) => id).filter(id => !childrenCheckedIds?.includes(id)) || [];
          const childrenChecked =
            childrenCheckedIds && childrenCheckedIds.length > 0 ? (allChecked ? brothersIds : childrenCheckedIds) : [];
          setCheckChilds([
            ...childrenChecked,
            ...(localChecked
              ? res.data?.map(({ id }) => id) || []
              : res.data?.filter(({ isSelected }) => isSelected).map(({ id }) => id) || []),
          ]);
          return null;
        })
        .catch(log);
    setOpen(prev => !prev);
  };

  useEffect(() => {
    if (providersArray && checkChilds) {
      setLocalChecked(checkChilds.length === providersArray?.length);
    }
  }, [checkChilds, providersArray]);

  useEffect(() => {
    if (clearAll) setCheckChilds([]);
  }, [clearAll]);

  useEffect(() => {
    if (allChecked) setCheckChilds(providersArray?.map(({ id }) => id) || []);
  }, [allChecked, providersArray]);

  useEffect(() => {
    if (!allChecked && isClearAddedRemoved) setCheckChilds([]);
  }, [allChecked, isClearAddedRemoved]);

  const changeCheckChildrenLocal = (e: React.ChangeEvent<HTMLInputElement>, check: boolean) => {
    const { value: childId } = e.target;
    const brothersIds = providersArray?.map(({ id }) => id).filter(id => id !== childId) || [];
    if (selectionId) {
      changeAddedRemovedArrayChildren &&
        changeAddedRemovedArrayChildren(check, childId, option.value, brothersIds, checkChilds);
    } else {
      changeCheckChildren && changeCheckChildren(e, option.value || '', check, brothersIds);
    }
  };

  const changeCheckLocal = (e: React.ChangeEvent<HTMLInputElement>, checkedLocal: boolean) => {
    if (selectionId && !isChildren && state.index > 0) {
      changeAddedRemovedArray && changeAddedRemovedArray(option.value, checkedLocal);
      setCheckChilds(checkedLocal && providersArray ? providersArray.map(({ id }) => id) : []);
      setLocalChecked(checkedLocal);
    } else if (state?.index === 0 && !isChildren) {
      changeAll(e, checkedLocal);
    } else {
      changeCheck(e, checkedLocal);
      if (isChildren)
        childrenSetCheckChilds(prev =>
          checkedLocal ? [...(prev || []), option.value] : (prev || []).filter(prevVal => prevVal !== option.value),
        );
    }
  };

  const openChildren = open && (option.providers?.length || data?.length);

  return (
    <Fragment key={key}>
      <Stack direction="row" gap="10px" sx={sx}>
        <Checkbox
          value={option?.value}
          checked={localChecked}
          onChange={changeCheckLocal}
          sx={{ padding: '0 0 0 12px' }}
          inputProps={{ ...buildDataTestIdAttribute(dataTestId, 'checkbox') } as InputHTMLAttributes<HTMLInputElement>}
        />
        <ListItem {...props} onClick={onClick} sx={{ padding: '11px 0 11px 0' }}>
          <Stack direction="row" justifyContent="space-between" width="100%">
            <Typography color="#4B5563" variant={withChildren ? 'label14Bold' : 'label14Medium'} lineHeight="18px">
              {option?.name || '-'}
            </Typography>
            {state?.index > 0 && withChildren ? (
              <IconButton
                sx={{
                  padding: 0,
                  transform: `rotate(${openChildren ? '-180deg' : 0})`,
                }}
                {...buildDataTestIdAttribute(dataTestId, 'button-expand')}
              >
                <ArrowDown />
              </IconButton>
            ) : null}
          </Stack>
        </ListItem>
      </Stack>

      {openChildren
        ? providersArray?.map((provider, index) => {
            const checkedLocal =
              checkChilds && checkChilds?.length > 0
                ? intersctionValue
                  ? checkChilds?.includes(provider.id)
                  : checkChilds.length > 0 && checkChilds?.includes(provider.id)
                : checked;

            return (
              <OptionItem
                sx={{ paddingLeft: '12px', width: 'auto' }}
                option={{
                  value: provider?.id,
                  key: provider?.id,
                  name: provider?.name,
                  isSelected: isClearAddedRemoved ? localChecked : provider.isSelected,
                }}
                changeAll={changeAll}
                changeCheck={changeCheckChildrenLocal}
                checked={checkedLocal}
                childrenSetCheckChilds={setCheckChilds}
                isChildren={true}
                dataTestId={buildDataTestId(dataTestId, `children-${index}`)}
              />
            );
          })
        : null}
    </Fragment>
  );
};

export const AutocompleteWithApply: FC<AutocompleteWithApplyProps> = ({
  value,
  placeholder,
  options,
  noOptionsText,
  startSearch,
  onApply,
  parentName,
  childName,
  withChildren,
  loading,
  isEnd,
  setValuesFilter,
  onSearch,
  isSuccess,
  salesRepresentativeId,
  addedArr,
  removedArr,
  intersectionTypeFormValue,
  initialIntersectionType,
  searchValue,
  inputPlaceholder,
  disabled,
  scopeName,
}) => {
  const { isFetching } = useContext(FetchingContext);
  const { selectionId } = useParams();
  const dispatch = useDispatch();

  const { setValue } = useFormContext();
  const allChecked: boolean = useSelector((state: RootState) => state.selectionScope[scopeName]);

  const dataTestId = buildFullDataTestId(DATA_TEST_ID_SELECTION, `input-${replaceCebabCase(scopeName)}`);

  const [isClearAddedRemoved, setIsClearAddedRemoved] = useState(!!value.intersectionType);
  const prevCheckValue = useRef(true);

  const [ids, setIds] = useState<(string | { [key: string]: string[] })[]>(value?.ids || []);

  const [providerDisableStates, setProviderDisableStates] = useState<{ [key: string]: boolean }>({});

  const [addedIds, setAddedIds] = useState<any[]>(addedArr || []);

  const [removedIds, setRemovedIds] = useState<any[]>(removedArr || []);

  const [open, setOpen] = useState(isSuccess || false);
  const [clearAll, setClearAll] = useState(false);

  const [inputValue, setInputValue] = useState(`All ${inputPlaceholder}`);

  const isSearch = useRef(false);

  useEffect(() => {
    if (intersectionTypeFormValue) setIsClearAddedRemoved(intersectionTypeFormValue !== IntersectionType.None);
  }, [intersectionTypeFormValue]);

  useEffect(() => {
    if (addedArr) setAddedIds(addedArr || []);
  }, [addedArr]);

  useEffect(() => {
    if (removedArr) setRemovedIds(removedArr || []);
  }, [removedArr]);

  useLayoutEffect(() => {
    if (selectionId ? isFetching : true) {
      setInputValue(allChecked && ids.length === 0 ? `All ${inputPlaceholder}` : `Selected ${inputPlaceholder}`);
    }
  }, [setInputValue, allChecked, ids.length, inputPlaceholder, isFetching, selectionId]);

  useEffect(() => {
    if (value?.ids) setIds(value?.ids);
  }, [value?.ids]);

  useEffect(() => {
    setIds([]);
  }, [allChecked]);

  const handleOnCloseSelect = useCallback(() => {
    setOpen(false);
    setInputValue(allChecked && ids.length === 0 ? `All ${inputPlaceholder}` : `Selected ${inputPlaceholder}`);
    if (searchValue)
      setValuesFilter &&
        setValuesFilter(prev => ({
          ...prev,
          search: '',
        }));
  }, [setValuesFilter, searchValue, allChecked, ids?.length, inputPlaceholder]);

  const handleApplyClick = useCallback(() => {
    onApply && onApply(ids, addedIds, removedIds);
    setInputValue(allChecked && ids.length === 0 ? `All ${inputPlaceholder}` : `Selected ${inputPlaceholder}`);
    setOpen(false);
    handleOnCloseSelect();
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [allChecked, JSON.stringify(ids), JSON.stringify(addedIds), JSON.stringify(removedIds), handleOnCloseSelect]);

  /* eslint-disable react-hooks/exhaustive-deps */

  const changeScope = (val: boolean) => {
    if (scopeName === SCOPE_PROVIDERS_NAME) {
      dispatch(setProvidersScope(val));
    } else {
      dispatch(setLaboratoriesScope(val));
    }
  };

  const handleCancelClick = useCallback(() => {
    setOpen(false);

    setIds(value?.ids || []);
    setAddedIds(addedArr || []);
    setRemovedIds(removedArr || []);
    handleOnCloseSelect();
    setIsClearAddedRemoved(false);
    setClearAll(false);
    changeScope(prevCheckValue.current || false);
  }, [value?.intersectionType, JSON.stringify(value?.ids), handleOnCloseSelect]);

  const handleClearClick = useCallback(() => {
    changeScope(false);
    setIds([]);
    setClearAll(true);
    setAddedIds([]);
    setRemovedIds([]);
    setIsClearAddedRemoved(true);
    setTimeout(() => setClearAll(false), 500);
  }, []);

  const changeCheck = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value: optionId } = e.target;
      setIds((prev: any) => {
        const cond =
          parentName && childName ? prev.some(prevVal => prevVal[parentName] === optionId) : prev.includes(optionId);

        return cond
          ? prev.filter(prevVal => (parentName && childName ? prevVal[parentName] !== optionId : prevVal !== optionId))
          : [...prev, parentName && childName ? { [parentName]: optionId, [childName]: [] } : optionId];
      });
    },
    [childName, parentName],
  );

  const changeCheckChildren = useCallback(
    (e, parentId, checked, brothersIds: string[]) => {
      const { value: childrenId } = e.target;

      const valueForEdit = { parentId, parentName, checked, brothersIds, childrenId, childName, allChecked };

      if (parentName && childName) {
        setIds((prev: any) => changeIdsChildren(prev, valueForEdit));
      }
    },
    [childName, parentName, allChecked],
  );

  const changeAll = useCallback(
    (_e, checked: boolean) => {
      changeScope(checked);
      setIds([]);
      setIsClearAddedRemoved(true);
      if (selectionId) {
        setAddedIds([]);
        setRemovedIds([]);
        if (withChildren) {
          setValue('createVisibility.addedFacilityVisibilities', []);
          setValue('createVisibility.removedFacilityVisibilities', []);
        }
      }
    },
    [selectionId, setValue, withChildren, options],
  );

  const changeAddedRemovedArray = useCallback(
    (optionId: string, checked: boolean) => {
      const valueForEdit = { parentName, childName, optionId };
      const removeAddedRemovedArr = prev => removedParentFromAddedRemoved(prev, valueForEdit);

      const includesId = prev => includesParentId(prev, valueForEdit);

      const withoutId = prev => parentWithoutId(prev, valueForEdit);

      const putId = prev => putParent(prev, valueForEdit);
      if (isClearAddedRemoved || allChecked) {
        setIds((prev: any) => {
          return includesId(prev) ? withoutId(prev) : putId(prev);
        });
      } else {
        if (checked) {
          const checkInRemoved = parentIncludesInAddedRemovedArr(removedIds, parentName, optionId);

          if (checkInRemoved) {
            setRemovedIds(removeAddedRemovedArr);
          } else setAddedIds(prev => putId(prev));
        } else {
          const checkInAdded = parentIncludesInAddedRemovedArr(addedIds, parentName, optionId);

          if (checkInAdded) {
            setAddedIds(removeAddedRemovedArr);
          } else setRemovedIds(prev => putId(prev));
        }
      }
    },
    [childName, parentName, isClearAddedRemoved, addedIds, removedIds, allChecked],
  );

  const changeAddedRemovedArrayChildren = useCallback(
    (checked, childrenId, parentId, brothersIds: string[], checkChilds: string[]) => {
      const childrenCount = brothersIds.length + 1;

      if (parentName && childName) {
        const valueForEdit = { parentId, parentName, childName, childrenId, brothersIds, childrenCount, checkChilds };
        const prevChecked = prev => setAddedRemovedArrChildren(prev, valueForEdit);

        const checkWithBrothers = prev => checkChildrenWithBrothers(prev, valueForEdit);

        const removeAddedRemovedArr = prev =>
          removedChildrenFromAddedRemoved(prev, parentName, childName, parentId, childrenId);

        if (isClearAddedRemoved || allChecked) {
          changeCheckChildren({ target: { value: childrenId } }, parentId, checked, brothersIds);
        } else if (checked) {
          const checkInRemoved = childrenIncludesInAddedRemovedArr(removedIds, valueForEdit);
          const parentChecked =
            parentIncludesInAddedRemovedArr(removedIds, parentName, parentId)?.[childName].length === 0;

          if (parentChecked) {
            setRemovedIds(checkWithBrothers);
          } else if (checkInRemoved) {
            setRemovedIds(removeAddedRemovedArr);
          } else setAddedIds(prevChecked);
        } else {
          const checkInAdded = childrenIncludesInAddedRemovedArr(addedIds, valueForEdit);
          const parentChecked =
            parentIncludesInAddedRemovedArr(addedIds, parentName, parentId)?.[childName].length === 0;

          if (parentChecked) {
            setAddedIds(checkWithBrothers);
          } else if (checkInAdded) {
            setAddedIds(removeAddedRemovedArr);
          } else setRemovedIds(prevChecked);
        }
      }
    },
    [childName, parentName, removedIds, addedIds, allChecked],
  );

  const scrollRestSelect = useCallback(() => {
    if (isEnd) return;
    setValuesFilter &&
      setValuesFilter(prevState => ({
        ...prevState,
        skip: +prevState.skip + PAGE_SIZE,
      }));
  }, [isEnd, setValuesFilter]);

  const scrollRestSelectDebounce = debounce(scrollRestSelect, 100);

  const handleOnScrollSelect = useCallback(
    (event: React.SyntheticEvent) => {
      if (isEnd && loading) {
        return;
      }
      const listboxNode = event.currentTarget;
      if (
        Math.ceil(listboxNode.scrollTop + listboxNode.clientHeight) > listboxNode.scrollHeight * 0.99 &&
        Math.ceil(listboxNode.scrollTop + listboxNode.clientHeight) <= listboxNode.scrollHeight * 1.01
      ) {
        scrollRestSelectDebounce();
      }
    },
    [isEnd, loading, scrollRestSelectDebounce],
  );

  const onInputChange = (_e: React.ChangeEvent<HTMLInputElement>, newVal: string, reason) => {
    if (reason === 'reset') return;
    if (newVal?.length > 0) isSearch.current = true;
    setInputValue(newVal);
    onSearch && onSearch(newVal);
  };

  useEffect(() => {
    if (open && !searchValue && (!allChecked || ids?.length > 0 || addedIds?.length > 0 || removedIds?.length > 0)) {
      setInputValue('');
    }
  }, [allChecked, open, ids?.length, addedIds?.length, removedIds?.length, searchValue]);

  const selectedOptionIds = options?.filter(option => option.isSelected).map(option => option.value);

  const allSelectedRemoved =
    scopeName === SCOPE_PROVIDERS_NAME
      ? selectedOptionIds?.every(id =>
          removedIds.some(removed => removed.facilityId === id && removed.orderingProviderIds.length === 0),
        )
      : selectedOptionIds?.every(id => removedIds.includes(id));

  const updateProviderDisableStates = (
    localChecked: boolean,
    checkChilds: Nullable<string[]>,
    data: IProvider[] | undefined,
    option: Option,
  ) => {
    if (option.name === 'All accounts') return;

    if (!localChecked && checkChilds && checkChilds?.length === 0) {
      setProviderDisableStates(prev => ({ ...prev, [option.name]: true }));
    } else if (!localChecked && checkChilds && checkChilds?.length > 0) {
      setProviderDisableStates(prev => ({ ...prev, [option.name]: false }));
    } else if (localChecked && checkChilds && data && checkChilds.length === data.length) {
      setProviderDisableStates(prev => ({ ...prev, [option.name]: false }));
    }
  };

  const hasDisabledProviders = Object.values(providerDisableStates).includes(true);
  const isExcludeType = initialIntersectionType === 'Exclude';
  const allOptionsSelected = ids.length + 1 === options?.length;
  const noAddedIds = addedIds.length === 0;
  const clearOrAllProvidersRemoved =
    isClearAddedRemoved ||
    (removedIds.length === 1 && removedIds[0].orderingProviderIds?.length === 0 ? allSelectedRemoved : true);
  const shouldDisableApplyDueToProviders =
    hasDisabledProviders && (!allChecked || (allChecked && isExcludeType && allOptionsSelected));
  const idsCondition = isExcludeType
    ? ids.length === options?.filter(option => option.value !== 'all').length
    : ids.length === 0;

  const disableApply =
    scopeName === SCOPE_PROVIDERS_NAME
      ? shouldDisableApplyDueToProviders && noAddedIds && idsCondition && clearOrAllProvidersRemoved
      : (!allChecked || (allChecked && isExcludeType && allOptionsSelected)) &&
        noAddedIds &&
        idsCondition &&
        (isClearAddedRemoved || allSelectedRemoved);

  const renderPaperComponent = useMemo(
    () =>
      ({
        children,
        handlecancelclick: cancel,
        handleclearclick: clear,
        handleapplyclick: apply,
        disabledApply,
        ...props
      }: PaperProps & {
        handlecancelclick: () => void;
        handleclearclick: () => void;
        handleapplyclick: () => void;
        disabledApply: boolean;
      }) => {
        return (
          <Paper
            onMouseDown={event => event.preventDefault()}
            sx={{
              boxShadow: '0px 8px 12px rgba(55, 65, 81, 0.1), 0px 0px 6px rgba(55, 65, 81, 0.1)',
              '& ul': {
                maxHeight: '320px',
              },
            }}
            {...props}
          >
            <Box>{children}</Box>
            <Stack padding="12px" direction="row" justifyContent="space-between" borderTop={border}>
              <Button
                color="secondary"
                size="small"
                variant="outlined"
                sx={classes.button}
                onClick={cancel}
                {...buildDataTestIdAttribute(dataTestId, 'button-cancel')}
              >
                Cancel
              </Button>
              <Stack direction="row" gap="12px">
                <Button
                  color="secondary"
                  variant="outlined"
                  size="small"
                  sx={classes.button}
                  onClick={clear}
                  {...buildDataTestIdAttribute(dataTestId, 'button-clear-all')}
                >
                  Clear all
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  size="small"
                  sx={classes.button}
                  onClick={apply}
                  disabled={disabledApply}
                  {...buildDataTestIdAttribute(dataTestId, 'button-apply')}
                >
                  Apply
                </Button>
              </Stack>
            </Stack>
          </Paper>
        );
      },
    [],
  );

  return (
    <Autocomplete
      renderOption={(
        props,
        option: {
          value: string;
          key?: string;
          name: string;
          isSelected?: boolean;
          providers?: { name: string; id: string }[];
        },
        state,
      ) => {
        const checkedFunction = () => {
          if (state.index === 0) {
            return allChecked && ids?.length === 0 && addedIds?.length === 0 && removedIds?.length === 0;
          } else if (allChecked) {
            return withChildren && parentName
              ? !ids.some(el => {
                  return el[parentName] === option.value;
                }) &&
                  !removedIds.some(el => {
                    return el[parentName] === option.value;
                  })
              : !ids.includes(option.value) && !removedIds.includes(option.value);
          } else
            return withChildren && parentName && childName
              ? ids.some(el => {
                  return el[parentName] === option.value && el[childName].length === 0;
                }) ||
                  addedIds.some(el => {
                    return el[parentName] === option.value;
                  }) ||
                  (option.isSelected &&
                    !isClearAddedRemoved &&
                    !removedIds?.some(el => {
                      return el[parentName] === option.value;
                    }))
              : ids.includes(option.value) ||
                  addedIds.includes(option.value) ||
                  (option.isSelected && !isClearAddedRemoved && !removedIds.includes(option.value));
        };

        const checked = checkedFunction();

        const checkedEdit = allChecked
          ? parentName && childName
            ? removedIds.find(id => id[parentName] === option.value)?.[childName]
            : []
          : parentName && childName
          ? addedIds.find(id => id[parentName] === option.value)?.[childName]
          : [];

        const childrenCheckedIds = selectionId
          ? checkedEdit
          : parentName && childName
          ? ids.find(id => id[parentName] === option.value)?.[childName]
          : [];

        const isSelected = allChecked ? true : option.isSelected;

        return (
          <OptionItem
            {...props}
            key={option.value}
            checked={checked}
            changeAll={changeAll}
            changeCheck={changeCheck}
            changeCheckChildren={changeCheckChildren}
            option={{ ...option, isSelected: selectionId ? isSelected : checked }}
            state={state}
            childrenCheckedIds={childrenCheckedIds}
            withChildren={withChildren}
            changeAddedRemovedArray={changeAddedRemovedArray}
            changeAddedRemovedArrayChildren={changeAddedRemovedArrayChildren}
            salesRepresentativeId={salesRepresentativeId}
            intersctionValue={allChecked}
            removedIds={removedArr}
            addedIds={addedArr}
            // checkedValues={interimCheckedValues}
            parentName={parentName}
            childName={childName}
            clearAll={clearAll}
            allChecked={allChecked && (ids?.length === 0 || addedIds?.length === 0 || removedIds?.length === 0)}
            isClearAddedRemoved={isClearAddedRemoved}
            updateProviderDisableStates={updateProviderDisableStates}
            dataTestId={buildDataTestId(dataTestId, `row-${state.index}`)}
          />
        );
      }}
      disabled={disabled}
      ListboxProps={{
        onScroll: handleOnScrollSelect,
        role: 'list-box',
        sx: {
          '& .MuiAutocomplete-option': {
            padding: '11px 12px 11px 0',
            '&[aria-selected="true"]': { backgroundColor: 'transparent' },
          },
        },
      }}
      loading={loading}
      value={[]}
      open={open}
      dataTestId={dataTestId}
      onOpen={() => {
        startSearch && startSearch();
        setOpen(true);
        prevCheckValue.current = allChecked;
      }}
      onClose={(_e, reason) => {
        if (reason === 'blur') handleCancelClick();
      }}
      componentsProps={{ popper: { sx: { zIndex: 1 } } }}
      popupIcon={
        <PopupIcon
          onClick={
            open
              ? () => {
                  handleCancelClick();
                }
              : undefined
          }
        />
      }
      filterOptions={x => x}
      PaperComponent={options?.length === 0 ? undefined : renderPaperComponent}
      slotProps={{
        paper: {
          handlecancelclick: handleCancelClick,
          handleclearclick: handleClearClick,
          handleapplyclick: handleApplyClick,
          disabledApply: disableApply,
        },
      }}
      noOptionsText={
        <ClickAwayListener onClickAway={handleCancelClick}>
          <Typography color="grey.400" variant="label14Medium">
            {searchValue
              ? 'There are no results matching your search. Try to change your search options'
              : noOptionsText || 'No options'}
          </Typography>
        </ClickAwayListener>
      }
      isOptionEqualToValue={() => {
        return true;
      }}
      options={options || []}
      multiple={undefined}
      disableClearable={true}
      placeholder={placeholder}
      onInputChange={onInputChange}
      inputValue={inputValue}
      withSearchIcon={false}
      sx={{}}
      onClick={undefined}
      inputPropsChange={undefined}
      showCustomClearIcon={false}
    />
  );
};
